/**
 * WebSocket Heartbeat Manager
 *
 * WebSocket 연결의 안정성을 보장하기 위한 Heartbeat(Ping/Pong) 매니저
 *
 * @example
 * ```typescript
 * const heartbeat = new WebSocketHeartbeat(ws, () => reconnect());
 * heartbeat.start(30000, 5000); // 30초마다 Ping, 5초 Pong 대기
 *
 * // Pong 수신 시
 * heartbeat.receivedPong();
 *
 * // 연결 종료 시
 * heartbeat.stop();
 * ```
 */
export class WebSocketHeartbeat {
  private ws: WebSocket;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private onReconnect: () => void;
  private name: string;

  /**
   * @param ws - WebSocket 인스턴스
   * @param onReconnect - Pong 타임아웃 시 호출될 재연결 콜백
   * @param name - 디버깅용 WebSocket 이름 (선택사항)
   */
  constructor(ws: WebSocket, onReconnect: () => void, name = 'WebSocket') {
    this.ws = ws;
    this.onReconnect = onReconnect;
    this.name = name;
  }

  /**
   * Heartbeat 시작
   *
   * @param interval - Ping 전송 간격 (밀리초, 기본값: 30000ms = 30초)
   * @param pongTimeout - Pong 대기 시간 (밀리초, 기본값: 5000ms = 5초)
   */
  start(interval = 30000, pongTimeout = 5000) {
    // 기존 타이머 정리 (중복 방지)
    this.stop();

    console.log(`[${this.name}] Heartbeat started (interval: ${interval}ms, timeout: ${pongTimeout}ms)`);

    this.heartbeatTimer = setInterval(() => {
      if (this.ws.readyState === WebSocket.OPEN) {
        // Ping 전송
        this.ws.send(JSON.stringify({ type: 'ping' }));
        console.log(`[${this.name}] Ping sent`);

        // Pong 대기 타이머 시작
        this.pongTimer = setTimeout(() => {
          console.warn(`[${this.name}] Pong timeout - reconnecting...`);

          // WebSocket 연결 종료 (onclose에서 재연결 처리)
          // 주의: ws.close()만 호출하면 onclose 이벤트에서 재연결이 처리됩니다
          // onReconnect()를 중복 호출하면 안 됩니다!
          this.ws.close();
        }, pongTimeout);
      }
    }, interval);
  }

  /**
   * Pong 메시지 수신 처리
   *
   * WebSocket의 onmessage 핸들러에서 호출
   */
  receivedPong() {
    // Pong 대기 타이머 취소
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    console.log(`[${this.name}] Pong received`);
  }

  /**
   * Heartbeat 정지
   *
   * WebSocket 연결 종료 시 반드시 호출하여 타이머 정리
   */
  stop() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
      console.log(`[${this.name}] Heartbeat stopped`);
    }

    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  /**
   * 현재 Heartbeat 실행 상태 확인
   */
  isRunning(): boolean {
    return this.heartbeatTimer !== null;
  }
}
